import React, { useState, useContext, useEffect } from 'react';
import { BsWhatsapp, BsTelephone, BsPerson } from 'react-icons/bs';
import { FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AttendancesContext } from '../../../../contexts/user/AttendancesContext';
import { Contact, CustomerWithContact } from '../../../../interfaces/Customers.interface';
import { LoginContext } from '../../../../contexts/global/LoginContext';
import api from '../../../../services/api';

interface VCardModalProps {
    open: boolean;
    onClose: () => void;
    contactName: string;
    contactPhone: string;
}

export const VCardModal = ({ open, onClose, contactName, contactPhone }: VCardModalProps) => {
    const { startAttendance, selectedClient } = useContext(AttendancesContext);
    const { user } = useContext(LoginContext);
    const [isLoading, setIsLoading] = useState(true);
    const [contactExists, setContactExists] = useState(false);
    const [contactData, setContactData] = useState<CustomerWithContact | null>(null);

    useEffect(() => {
        const resetState = () => {
            setIsLoading(true);
            setContactExists(false);
            setContactData(null);
        };

        if (!open) {
            resetState();
            return;
        }

        if (!contactPhone) {
            setIsLoading(false); 
            return;
        }

        const findContact = async () => {
            resetState(); 
            const formattedPhone = contactPhone.replace(/\D/g, "");

            try {
                const contactRes = await api.get<Contact>(`/contacts/findByContact/${formattedPhone}`);
                const foundContact = contactRes.data;

                if (foundContact && foundContact.CODIGO) {
                    setContactExists(true); 

                    if (foundContact.CODIGO_CLIENTE !== -1) {
                        const customerRes = await api.get(`/customers/custom/filter`, {
                            params: { field: 'CODIGO', value: foundContact.CODIGO_CLIENTE }
                        });
                        const foundCustomer = customerRes.data.data[0];

                        const customerWithContact: CustomerWithContact = {
                            CODIGO: foundCustomer.CODIGO,
                            RAZAO: foundCustomer.RAZAO,
                            FANTASIA: foundCustomer.FANTASIA,
                            PESSOA: foundCustomer.PESSOA,
                            ATIVO: foundCustomer.ATIVO,
                            COD_ERP: foundCustomer.COD_ERP,
                            CPF_CNPJ: foundCustomer.CPF_CNPJ,
                            OPERADOR: foundCustomer.OPERADOR,
                            CODIGO_CONTATO: foundContact.CODIGO,
                            NOME: foundContact.NOME,
                            NUMERO: foundContact.NUMERO,
                            BEING_ATTENDED: false,
                            OPERATOR_NAME: '',
                            FIDELIZADO: '',
                        };
                        setContactData(customerWithContact);
                    } else {
                        const customerWithContact: CustomerWithContact = {
                            CODIGO: -1,
                            RAZAO: 'Não Associado',
                            FANTASIA: '',
                            PESSOA: 'JUR',
                            ATIVO: 'SIM',
                            COD_ERP: null,
                            CPF_CNPJ: '',
                            OPERADOR: 0,
                            CODIGO_CONTATO: foundContact.CODIGO,
                            NOME: foundContact.NOME,
                            NUMERO: foundContact.NUMERO,
                            BEING_ATTENDED: false,
                            OPERATOR_NAME: '',
                            FIDELIZADO: '',
                        };
                        setContactData(customerWithContact);
                    }
                } else {
                    setContactExists(false);
                    setContactData(null);
                }
            } catch (error) {
                console.log("Contato não encontrado, pode ser criado.");
                setContactExists(false);
                setContactData(null);
            } finally {
                setIsLoading(false);
            }
        };

        findContact();
    }, [open, contactPhone]);

    const handleAddAndStartChat = async () => {
        setIsLoading(true);
        try {
            const newContactData = {
                NOME: contactName,
                NUMERO: contactPhone.replace(/\D/g, ""),
                CODIGO_CLIENTE: -1
            };
            
            const response = await api.post<Contact>(`/contacts`, newContactData);
            const newContact = response.data;
            
            toast.success("Contato adicionado com sucesso!");

            startAttendance(
                newContact.CODIGO_CLIENTE,
                newContact.CODIGO,
                newContact.NUMERO,
                null
            );

            onClose();
        } catch (error) {
            console.error("Erro ao adicionar e iniciar conversa:", error);
            toast.error("Falha ao adicionar contato.");
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleStartConversation = () => {
        if (!user || !contactData) {
            alert("Erro: Dados de usuário ou contato ausentes.");
            return;
        }
        
        selectedClient.set(contactData);
        
        startAttendance(
            contactData.CODIGO,
            contactData.CODIGO_CONTATO,
            contactData.NUMERO,
            null
        );

        onClose();
    };

    const handleJustAdd = async () => {
        setIsLoading(true);
        try {
            const newContactData = {
                NOME: contactName,
                NUMERO: contactPhone.replace(/\D/g, ""),
                CODIGO_CLIENTE: -1
            };
            await api.post<Contact>(`/contacts`, newContactData);
            toast.success("Contato adicionado com sucesso!");
            onClose();
        } catch (error) {
            console.error("Erro ao adicionar contato:", error);
            toast.error("Falha ao adicionar contato.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
        }}>
            <div style={{
                backgroundColor: '#fff',
                padding: '16px',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                maxWidth: '280px',
                width: '100%',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
                {isLoading ? (
                    <FaSpinner className="spin-animation" size={32} />
                ) : (
                    <>
                        <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            backgroundColor: '#1976d2',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: '#fff',
                            marginBottom: '12px',
                        }}>
                            <BsPerson size={32} />
                        </div>
        
                        <h3 style={{ margin: '0 0 4px', fontWeight: 'bold', fontSize: '1.2rem' }}>
                            {contactExists && contactData?.NOME ? contactData.NOME : contactName}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <BsTelephone size={14} style={{ marginRight: '6px', color: '#666' }} />
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>{contactPhone}</span>
                        </div>
        
                        <div style={{
                            marginTop: '16px',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}>
                            {contactExists ? (
                                <button
                                    onClick={handleStartConversation}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                        padding: '8px 16px', border: 'none', borderRadius: '8px',
                                        backgroundColor: '#25D366', color: '#fff', fontWeight: 'bold',
                                        cursor: 'pointer', transition: 'background-color 0.3s', fontSize: '0.9rem'
                                    }}
                                >
                                    <BsWhatsapp size={18} />
                                    Iniciar Conversa
                                </button>
                            ) : (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={handleJustAdd}
                                        style={{
                                            flex: 1, padding: '8px 16px', border: '1px solid #ddd',
                                            borderRadius: '8px', backgroundColor: '#fff', color: '#333',
                                            fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.3s', fontSize: '0.9rem'
                                        }}
                                        disabled={isLoading}
                                    >
                                        Adicionar
                                    </button>
                                    <button
                                        onClick={handleAddAndStartChat}
                                        style={{
                                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            gap: '8px', padding: '8px 16px', border: 'none',
                                            borderRadius: '8px', backgroundColor: '#25D366', color: '#fff',
                                            fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.3s', fontSize: '0.9rem'
                                        }}
                                        disabled={isLoading}
                                    >
                                        Conversar
                                    </button>
                                </div>
                            )}
                            <button
                                onClick={onClose}
                                style={{
                                    marginTop: '4px', padding: '8px 16px', border: '1px solid #ddd',
                                    borderRadius: '8px', backgroundColor: 'transparent', color: '#666',
                                    cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem'
                                }}
                            >
                                Fechar
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};